import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconButton, InputAdornment, SxProps, Typography } from '@mui/material'
import type { TypographyProps } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import {
  ActiveHeaderContainer,
  InactiveHeaderContainer,
  FullWidthTextField,
} from './SearchHeader.style'

export const SEARCH_ACTIONS = {
  TOGGLE: 'toggle' as const,
  OFF: 'off' as const,
}
export type SearchAction = typeof SEARCH_ACTIONS[keyof typeof SEARCH_ACTIONS]

export interface SearchHeaderProps {
  active: boolean
  label: string
  placeholder?: string
  value: string
  onToggle: (action?: SearchAction) => void
  onChange: (value: string) => void
  onClear: () => void
  inputRef?: React.RefObject<HTMLInputElement>
  debounceMs?: number
  activeContainerSx?: SxProps
  inactiveContainerSx?: SxProps
  textFieldSx?: SxProps
  labelTypographyProps?: Omit<TypographyProps, 'children'>
}

const SearchHeader = ({
  active,
  label,
  placeholder = label,
  value,
  onToggle,
  onChange,
  onClear,
  inputRef,
  debounceMs = 0,
  activeContainerSx,
  inactiveContainerSx,
  textFieldSx,
  labelTypographyProps,
}: SearchHeaderProps) => {
  const fallbackRef = useRef<HTMLInputElement>(null)
  const effectiveRef = inputRef || fallbackRef
  const debounceIdRef = useRef<number | null>(null)
  const pendingValueRef = useRef<string | null>(null)

  const [draftValue, setDraftValue] = useState(value)

  useEffect(() => {
    if (debounceMs <= 0) return

    setDraftValue(prev => (prev === value ? prev : value))
  }, [debounceMs, value])

  useEffect(() => {
    if (!active) return

    const id = window.setTimeout(() => {
      try {
        effectiveRef.current?.focus()
      } catch {
        // noop
      }
    }, 0)

    return () => window.clearTimeout(id)
  }, [active]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      if (debounceIdRef.current !== null) {
        window.clearTimeout(debounceIdRef.current)
      }
    },
    []
  )

  const displayedValue = debounceMs > 0 ? draftValue : value

  const scheduleOnChange = useCallback(
    (next: string) => {
      if (debounceMs <= 0) {
        onChange(next)
        return
      }

      pendingValueRef.current = next

      if (debounceIdRef.current !== null) {
        window.clearTimeout(debounceIdRef.current)
      }

      debounceIdRef.current = window.setTimeout(() => {
        const pendingValue = pendingValueRef.current

        pendingValueRef.current = null
        debounceIdRef.current = null

        if (pendingValue !== null) {
          onChange(pendingValue)
        }
      }, debounceMs)
    },
    [debounceMs, onChange]
  )

  const cancelPendingChange = useCallback(() => {
    if (debounceIdRef.current !== null) {
      window.clearTimeout(debounceIdRef.current)
    }

    debounceIdRef.current = null
    pendingValueRef.current = null
  }, [])

  return active ? (
    <ActiveHeaderContainer sx={activeContainerSx}>
      <FullWidthTextField
        inputRef={effectiveRef}
        size="small"
        placeholder={placeholder}
        value={displayedValue}
        onChange={event => {
          const next = event.target.value

          if (debounceMs > 0) {
            setDraftValue(next)
          }

          scheduleOnChange(next)
        }}
        onClick={event => event.stopPropagation()}
        onBlur={event => {
          if (!event.target.value) {
            onToggle(SEARCH_ACTIONS.OFF)
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={event => {
                  event.stopPropagation()
                  cancelPendingChange()

                  if (debounceMs > 0) {
                    setDraftValue('')
                  }

                  onClear()
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={textFieldSx}
      />
    </ActiveHeaderContainer>
  ) : (
    <InactiveHeaderContainer sx={inactiveContainerSx}>
      <Typography variant="body2" {...labelTypographyProps}>
        {label}
      </Typography>

      <IconButton
        size="small"
        onClick={event => {
          event.stopPropagation()
          onToggle(SEARCH_ACTIONS.TOGGLE)
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
    </InactiveHeaderContainer>
  )
}

export default SearchHeader
